import { useCallback, useEffect, useState } from 'react';
import { useApi, useMe } from '../context';
import type { Line } from '../types';

export interface InventoryRow {
  kind: 'auto_attendant' | 'call_queue' | 'resource_account' | 'phone_number' | 'licence';
  key: string;
  name: string;
  data: Record<string, unknown>;
  synced_at: string;
}
interface Op {
  id: string;
  kind: 'create_account' | 'assign_licence' | 'move_number' | 'remove_number';
  params: Record<string, unknown>;
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  requested_at: string;
  finished_at: string | null;
  result: Record<string, unknown> | null;
  error: string | null;
  requested_by_name: string | null;
}

export const accountType = (r: InventoryRow) => String(r.data['type'] ?? 'other');
export const typeLabel: Record<string, string> = { auto_attendant: 'AA', call_queue: 'CQ', bot: 'Bot', other: 'Other' };

/**
 * Teams automation: what the job knows (inventory), what it has been asked to do (ops),
 * and the guided "new bot line" flow that turns the cutover into portal actions.
 */
export function TeamsPanel({ lines, onLinesChanged }: { lines: Line[]; onLinesChanged: () => void }) {
  const api = useApi();
  const me = useMe();
  const isAdmin = me.roles.includes('admin');
  const [inventory, setInventory] = useState<InventoryRow[]>([]);
  const [ops, setOps] = useState<Op[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    api.get<InventoryRow[]>('/api/admin/teams/inventory').then(setInventory, (e: Error) => setError(e.message));
    api.get<Op[]>('/api/admin/teams/ops').then(setOps, (e: Error) => setError(e.message));
  }, [api]);
  useEffect(load, [load]);
  useEffect(() => {
    if (!ops.some((o) => o.status === 'queued' || o.status === 'running')) return;
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [ops, load]);

  const licence = inventory.find((r) => r.kind === 'licence');
  const syncedAt = inventory[0]?.synced_at;
  const accounts = inventory.filter((r) => r.kind === 'resource_account');
  const numbers = inventory.filter((r) => r.kind === 'phone_number');
  const botAccounts = accounts.filter((r) => accountType(r) === 'bot');

  async function queue(kind: Op['kind'], params: Record<string, unknown>) {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/admin/teams/ops', { kind, params });
      await api.post('/api/admin/teams/run', {}).catch(() => undefined); // best effort; schedule covers it
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const latestSync = (inv: InventoryRow[]) =>
    inv.reduce((m, r) => Math.max(m, new Date(r.synced_at).getTime() || 0), 0);

  /** Start the job, then poll until the inventory's synced_at advances (or time out). */
  async function syncNow() {
    setError(null); setSyncMsg(null);
    const baseline = latestSync(inventory);
    setSyncing(true);
    try {
      await api.post('/api/admin/teams/run', {});
    } catch (e) { setSyncing(false); setError((e as Error).message); return; }
    const startedAt = Date.now();
    const poll = async () => {
      const inv = await api.get<InventoryRow[]>('/api/admin/teams/inventory').catch(() => null);
      if (inv) {
        setInventory(inv);
        if (latestSync(inv) > baseline) {
          setSyncing(false);
          setSyncMsg(`Synced at ${new Date(latestSync(inv)).toLocaleTimeString()}`);
          load();
          return;
        }
      }
      if (Date.now() - startedAt > 90_000) {
        setSyncing(false);
        setSyncMsg('Sync started — the inventory should refresh shortly.');
        return;
      }
      window.setTimeout(poll, 5000);
    };
    window.setTimeout(poll, 4000);
  }

  return (
    <div>
      {error && <p className="error">{error}</p>}
      <div className="stats">
        <div className="stat"><div className="num">{accounts.length}</div>Resource accounts</div>
        <div className="stat"><div className="num">{numbers.length}</div>Numbers</div>
        <div className="stat">
          <div className="num">{licence ? `${licence.data['used']}/${licence.data['total']}` : '—'}</div>
          Resource-account licences used
        </div>
        <div className="stat"><div className="num">{syncedAt ? new Date(syncedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}</div>Last sync</div>
      </div>
      {licence && Number(licence.data['used']) >= Number(licence.data['total']) && (
        <p className="error">No free Teams Phone Resource Account licences — add some in the Microsoft 365 admin centre before creating bot lines.</p>
      )}
      {isAdmin && (
        <p>
          <button onClick={syncNow} disabled={busy || syncing}>{syncing ? 'Syncing…' : 'Sync now'}</button>{' '}
          {syncing && <span className="sub">⏳ Fetching the latest from Teams…</span>}
          {!syncing && syncMsg && <span className="sub">✓ {syncMsg}</span>}
        </p>
      )}

      {isAdmin && <NewBotLine inventory={inventory} lines={lines} botAccounts={botAccounts} onQueue={queue} busy={busy} onLinesChanged={onLinesChanged} />}

      <div className="filters">
        <h3 style={{ margin: 0 }}>Operations</h3>
        {isAdmin && ops.some((o) => o.status === 'succeeded' || o.status === 'failed') && (
          <button
            className="danger"
            disabled={busy || ops.some((o) => o.status === 'queued' || o.status === 'running')}
            title={ops.some((o) => o.status === 'queued' || o.status === 'running') ? 'Wait for in-progress operations to finish' : 'Remove finished operations'}
            onClick={() => {
              if (confirm('Clear all finished (succeeded/failed) operations from the list? Queued or running operations are kept.')) {
                setBusy(true);
                api.delete('/api/admin/teams/ops').then(load, (e: Error) => setError(e.message)).finally(() => setBusy(false));
              }
            }}
          >
            Clear finished operations
          </button>
        )}
      </div>
      <table>
        <thead><tr><th>Requested</th><th>Operation</th><th>Details</th><th>Status</th><th>Result</th></tr></thead>
        <tbody>
          {ops.map((o) => (
            <tr key={o.id}>
              <td>{new Date(o.requested_at).toLocaleString()}<div className="sub">{o.requested_by_name ?? 'job'}</div></td>
              <td>{o.kind.replace('_', ' ')}</td>
              <td><code>{Object.entries(o.params).map(([k, v]) => `${k}=${String(v)}`).join(' ')}</code></td>
              <td><span className={`badge ${o.status === 'succeeded' ? 'completed' : o.status === 'failed' ? 'missed' : 'answered'}`}>{o.status}</span></td>
              <td>{o.error ? <span className="error">{o.error}</span> : o.result ? <code>{Object.entries(o.result).map(([k, v]) => `${k}=${String(v)}`).join(' ')}</code> : '—'}</td>
            </tr>
          ))}
          {ops.length === 0 && <tr><td colSpan={5}>No operations yet</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/** Guided cutover for one line: create bot account → licence → move number → directory row. */
function NewBotLine({
  inventory, lines, botAccounts, onQueue, busy, onLinesChanged,
}: {
  inventory: InventoryRow[]; lines: Line[]; botAccounts: InventoryRow[];
  onQueue: (kind: Op['kind'], params: Record<string, unknown>) => Promise<void>; busy: boolean; onLinesChanged: () => void;
}) {
  const api = useApi();
  const [name, setName] = useState('');
  const [upn, setUpn] = useState('');
  const [accountOid, setAccountOid] = useState('');
  const [number, setNumber] = useState('');
  const [target, setTarget] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  const numbers = inventory.filter((r) => r.kind === 'phone_number');
  const targets = inventory.filter((r) => r.kind === 'resource_account' && ['auto_attendant', 'call_queue'].includes(accountType(r)));
  const selectedAccount = botAccounts.find((a) => a.key === accountOid);
  const selectedNumber = numbers.find((n) => n.key === number);

  async function addDirectoryRow() {
    if (!selectedAccount) return;
    await api.post('/api/admin/lines', {
      resourceAccountOid: selectedAccount.key,
      number: number || null,
      name: name || selectedAccount.name,
      ringTargets: target ? `app:${target}` : '',
    });
    setMsg('Directory row added — the bot picks it up within a few seconds.');
    onLinesChanged();
  }

  const existingLine = lines.find((l) => l.resource_account_oid === accountOid);

  return (
    <div className="wizard">
      <h4>Provision a new bot account &amp; number</h4>
      <p className="hint">
        Each step queues a Teams operation the job runs (watch Operations below): <strong>1</strong> create the
        bot's resource account → <strong>2</strong> assign its licence → <strong>3</strong> move the number onto it →
        <strong>4</strong> add the line. To undo, use “Remove from bot” on the number.
      </p>

      <div className="filters">
        <strong>1.</strong>
        <input placeholder="Display name, e.g. HLHC On-Call London" value={name} onChange={(e) => setName(e.target.value)} />
        <input placeholder="UPN, e.g. oncall-london@homelinkhealthcare.co.uk" style={{ width: '22rem' }} value={upn} onChange={(e) => setUpn(e.target.value)} />
        <button disabled={busy || !name || !upn} onClick={() => onQueue('create_account', { upn, displayName: name })}>Create bot account</button>
      </div>

      <div className="filters">
        <strong>2.</strong>
        <select value={accountOid} onChange={(e) => setAccountOid(e.target.value)}>
          <option value="">Select bot account…</option>
          {botAccounts.map((a) => <option key={a.key} value={a.key}>{a.name} ({String(a.data['upn'])}){a.data['phoneNumber'] ? ` — ${String(a.data['phoneNumber'])}` : ''}</option>)}
        </select>
        <button disabled={busy || !accountOid} onClick={() => onQueue('assign_licence', { oid: accountOid })}>Assign licence</button>
      </div>

      <div className="filters">
        <strong>3.</strong>
        <select value={number} onChange={(e) => setNumber(e.target.value)}>
          <option value="">Select number…</option>
          {numbers.map((n) => <option key={n.key} value={n.key}>{n.key} — currently {String(n.data['assignedName'])} [{typeLabel[String(n.data['holderType'])] ?? 'Other'}]</option>)}
        </select>
        <button
          disabled={busy || !number || !selectedAccount}
          onClick={() => onQueue('move_number', { number, toUpn: String(selectedAccount!.data['upn']), fromUpn: selectedNumber?.data['assignedUpn'] ? String(selectedNumber.data['assignedUpn']) : undefined })}
        >
          Move number to bot account
        </button>
        {selectedNumber && String(selectedNumber.data['holderType']) === 'bot' && (
          <button className="danger" disabled={busy} onClick={() => onQueue('remove_number', { upn: String(selectedNumber.data['assignedUpn']) })}>
            Remove from bot (start rollback)
          </button>
        )}
      </div>

      <div className="filters">
        <strong>4.</strong>
        <select value={target} onChange={(e) => setTarget(e.target.value)}>
          <option value="">Ring target: existing attendant / queue…</option>
          {targets.map((t) => <option key={t.key} value={t.key}>[{typeLabel[accountType(t)]}] {t.name}</option>)}
        </select>
        <button className="primary" disabled={busy || !selectedAccount || !!existingLine} onClick={addDirectoryRow}>
          {existingLine ? 'Directory row exists' : 'Add directory row'}
        </button>
      </div>
      {msg && <p className="hint">{msg}</p>}
    </div>
  );
}
